import { useState } from "react";
import type { ImapPreset } from "../../../shared/types";
import { useConnectImap } from "../../hooks/useApi";

const PRESET_META: Record<ImapPreset, { label: string; appPwUrl?: string; hint: string }> = {
  icloud: {
    label: "iCloud",
    appPwUrl: "https://appleid.apple.com",
    hint: "Generate an app-specific password at appleid.apple.com → Sign-In & Security → App-Specific Passwords.",
  },
  yahoo: {
    label: "Yahoo",
    appPwUrl: "https://login.yahoo.com/account/security",
    hint: "Generate an app password in Yahoo Account Security → Generate app password.",
  },
  generic: {
    label: "IMAP",
    hint: "Enter your provider's IMAP and SMTP host/port and an app password.",
  },
};

export function ImapConnectDialog({
  preset,
  onConnected,
  onClose,
}: {
  preset: ImapPreset;
  onConnected: (accountId: number) => void;
  onClose: () => void;
}) {
  const meta = PRESET_META[preset];
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [host, setHost] = useState("");
  const [port, setPort] = useState("993");
  const [smtpHost, setSmtpHost] = useState("");
  const [smtpPort, setSmtpPort] = useState("587");
  const connect = useConnectImap();

  const submit = () => {
    connect.mutate(
      {
        preset,
        email: email.trim(),
        password,
        ...(preset === "generic"
          ? { host: host.trim(), port: Number(port), smtpHost: smtpHost.trim(), smtpPort: Number(smtpPort) }
          : {}),
      },
      { onSuccess: (r) => onConnected(r.id) },
    );
  };

  const inputClass =
    "w-full rounded border border-stroke px-2.5 py-1.5 text-[13px] outline-none focus:border-accent";
  const canSubmit =
    email.trim() &&
    password &&
    (preset !== "generic" || (host.trim() && smtpHost.trim() && port && smtpPort));

  return (
    <div className="fixed inset-0 z-50 grid place-items-center bg-black/30" onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div
        className="flex w-[440px] max-w-[92vw] flex-col gap-2.5 rounded-lg border border-stroke bg-white p-4 shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <h2 className="text-[15px] font-semibold">Connect {meta.label} mailbox</h2>
        <p className="text-[12px] text-ink-muted">
          {meta.hint}
          {meta.appPwUrl && (
            <>
              {" "}
              <a
                href={meta.appPwUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="text-accent hover:underline"
              >
                Open settings
              </a>
            </>
          )}
        </p>

        <input
          className={inputClass}
          placeholder="Email address"
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          autoFocus
        />
        <input
          className={inputClass}
          placeholder="App-specific password"
          type="password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
        />

        {preset === "generic" && (
          <div className="grid grid-cols-2 gap-2">
            <input
              className={inputClass}
              placeholder="IMAP host"
              value={host}
              onChange={(e) => setHost(e.target.value)}
            />
            <input
              className={inputClass}
              placeholder="IMAP port"
              value={port}
              onChange={(e) => setPort(e.target.value)}
            />
            <input
              className={inputClass}
              placeholder="SMTP host"
              value={smtpHost}
              onChange={(e) => setSmtpHost(e.target.value)}
            />
            <input
              className={inputClass}
              placeholder="SMTP port"
              value={smtpPort}
              onChange={(e) => setSmtpPort(e.target.value)}
            />
          </div>
        )}

        {connect.error && (
          <p className="text-[12px] text-red-700">{connect.error.message}</p>
        )}

        <div className="flex justify-end gap-2 pt-1">
          <button
            onClick={onClose}
            className="rounded border border-stroke px-4 py-1.5 text-[13px] hover:bg-black/5"
          >
            Cancel
          </button>
          <button
            onClick={submit}
            disabled={!canSubmit || connect.isPending}
            className="rounded bg-accent px-4 py-1.5 text-[13px] font-medium text-white hover:bg-accent-hover disabled:opacity-50"
          >
            {connect.isPending ? "Connecting…" : "Connect"}
          </button>
        </div>
      </div>
    </div>
  );
}
